import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UnsubscribeAttentionModal } from '@pages/Subscriptions/modals/UnsubscribeAttention.modal'
import { UnsubscribeConfirmationModal } from '@pages/Subscriptions/modals/UnsubscribeConfirmation.modal'

import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { ExpandableRow } from '@ui/v2/Table/Table'

import { isLastBusinessDay } from '@utils/date.utils'

import { SubscriptionBundleDto } from '@model/SubscriptionBundleDto'
import { SubscriptionCartItem } from '@model/SubscriptionCart'
import { SubscriptionRequestState } from '@model/SubscriptionRequestState'
import { SubscriptionRequestTypeEnum } from '@model/SubscriptionRequestTypeEnum'
import { Subscription, SubscriptionDetail } from '@model/SubscriptionTypes'
import {
  SubscriptionRequestType,
  SubscriptionTimeInForce,
  UpdateSubscriptionRequest,
} from '@model/UpdateSubscriptionRequest'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { Box } from '@shared/components/Box'
import { ProductServiceModal } from '@shared/modals/ProductService.modal'

import { PriceRange } from '../SubscriptionsPriceRange'
import {
  SubscriptionTimeInForceModal,
  SubscriptionTimeInForceModalProps,
} from '../modals/SubscriptionTimeInForceType.modal'
import { CancelOrProcessingButton } from './ProductActions'
import { SubscriptionDetailMessage } from './SubscriptionDetailMessage'
import { ProductStatusIcon, isProductStatus } from './SubscriptionGroup'
import { ConfirmCancelSubscriptionModal } from './modals/ConfirmCancelSubscription.modal'
import { PendingUnsubscriptionModal } from './modals/PendingUnsubscription.modal'
import { ResubscribeModal } from './modals/Resubscribe.modal'
import { SubscriptionDetailModal } from './modals/SubscriptionDetail.modal'
import {
  allowSubDowngrade,
  allowSubUpgrade,
  getBadgeSubscriptionLevel,
  getSubscriptionDefaultLevel,
  hasActivatingSubscription,
  hasLevelInCart,
  hasPendingDowngrade,
  hasPendingUnsubscriptionRequest,
  hasPendingUpgrade,
  isLevelDowngrade,
  isSubscriptionTimeInForceImmediate,
} from './subscriptionUtils'

import styles from './MarketDataPage.module.scss'

const isCancellationRequested = (requestType: string | undefined) =>
  requestType === SubscriptionRequestTypeEnum.CancelExistingSubscription

const getSubscriptionStatus = (
  bundle: SubscriptionBundleDto,
  subscribedBundles: SubscriptionBundleDto[]
) => {
  const isBundleSubscribed = subscribedBundles?.find(
    (subscribedBundle) =>
      subscribedBundle.id === bundle.id &&
      subscribedBundle?.subscriptions?.some(
        (subBundle) =>
          subBundle?.activeSubscriptionRequest &&
          subBundle?.subscriptionRequest?.state.name !== SubscriptionRequestState.Pending
      )
  )
  if (isBundleSubscribed) {
    return 'subscribed'
  }

  if (bundle.subscriptionRequest) {
    return bundle.subscriptionRequest.state.name
  }
  return 'not_subscribed'
}

const getSubscriptionDetailStatus = (subscriptionDetail: SubscriptionDetail) => {
  if (
    subscriptionDetail?.activeSubscriptionRequest &&
    subscriptionDetail?.subscriptionRequest?.state.name !== SubscriptionRequestState.Pending
  ) {
    return 'subscribed'
  }
  if (subscriptionDetail.subscriptionRequest) {
    return subscriptionDetail.subscriptionRequest.state.name
  }
  return 'not_subscribed'
}

interface BundleSubscriptionDetailProps {
  showUpgrade?: boolean
  showDowngrade?: boolean
  selectedSubscription: Subscription
  handleOnSubscribeLevelChange(
    subscription: Subscription,
    subscriptionLevel: SubscriptionDetail
  ): void
  selectedSubscriptionDetail: SubscriptionDetail
  setSelectedSubscriptionDetail(SubscriptionDetail: SubscriptionDetail | undefined): void
  setPendingUnsubscriptionInfoModal(visible: boolean): void
}

const BundleSubscriptionDetail: React.FC<BundleSubscriptionDetailProps> = ({
  selectedSubscription,
  selectedSubscriptionDetail,
  setPendingUnsubscriptionInfoModal,
}) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()

  const defaultSubscriptionLevel = useMemo(
    () => getSubscriptionDefaultLevel(selectedSubscription) || selectedSubscription.details[0],
    [selectedSubscription]
  )

  const currentValue = selectedSubscriptionDetail
    ? `${selectedSubscriptionDetail.name} - ${formatMoney(selectedSubscriptionDetail.amount)}/${t(
        'Subscriptions.mth'
      )}`
    : `${defaultSubscriptionLevel?.name} - ${formatMoney(defaultSubscriptionLevel?.amount)}/${t(
        'Subscriptions.mth'
      )}`

  return (
    <>
      {selectedSubscriptionDetail && <span>{currentValue}</span>}
      <SubscriptionDetailMessage
        selectedSubscription={selectedSubscription}
        onSetPendingUnsubscriptionInfoModal={setPendingUnsubscriptionInfoModal}
      />
    </>
  )
}

interface BundleStatusProps {
  subscribedBundles: SubscriptionBundleDto[]
  bundle: SubscriptionBundleDto
  onSetIsExpanded(visible: boolean): void
  isExpanded: boolean
}

const BundleStatus: React.FC<BundleStatusProps> = ({
  bundle,
  subscribedBundles,
  onSetIsExpanded,
  isExpanded,
}) => {
  const { t } = useTranslation()
  const subscriptionStatus = getSubscriptionStatus(bundle, subscribedBundles)

  switch (subscriptionStatus) {
    case 'subscribed':
      return (
        <Badge type='green' contrast='low' size='sm'>
          {t('Subscriptions.Active')}
        </Badge>
      )

    case SubscriptionRequestState.DeductingFunds:
    case SubscriptionRequestState.InProcess:
      return isExpanded ? null : (
        <Badge type='yellow' contrast='low' size='sm'>
          {t('Subscriptions.Processing')}
        </Badge>
      )

    case SubscriptionRequestState.Failed:
      return isExpanded ? null : (
        <Button.Primary size='sm' onClick={() => onSetIsExpanded(true)}>
          {t('Subscriptions.Subscribe')}
        </Button.Primary>
      )

    case SubscriptionRequestState.Pending: {
      const subscriptionRequest = bundle.subscriptionRequest
      if (!subscriptionRequest) return null

      const isImmediate = isSubscriptionTimeInForceImmediate(subscriptionRequest)
      if (isImmediate) {
        return (
          <Badge type='yellow' contrast='low' size='sm'>
            {t('Subscriptions.Processing')}
          </Badge>
        )
      }

      if (
        subscriptionRequest?.type?.name === SubscriptionRequestTypeEnum.CancelExistingSubscription
      ) {
        return (
          <Badge type='yellow' contrast='low' size='sm'>
            {t('Subscriptions.Pending')}
          </Badge>
        )
      }

      const cancellation = isCancellationRequested(subscriptionRequest?.type.name)
      return (
        <Badge type='yellow' contrast='low' size='sm'>
          {t(cancellation ? 'Subscriptions.Pending Cancellation' : 'Subscriptions.Pending')}
        </Badge>
      )
    }

    case 'not_subscribed':
    default:
      return isExpanded ? null : (
        <Button.Primary size='sm' onClick={() => onSetIsExpanded(true)}>
          {t('Subscriptions.Subscribe')}
        </Button.Primary>
      )
  }
}

interface LevelActionProps {
  onSetUnsubscribeConfirmationModal(): void
  onSetUnsubscribeAttentionConfirmationModal(): void
  onSetSubscriptionTypeModal(visible: boolean): void
  onResubscribeBundle(): void
  selectedSubscriptionDetail: SubscriptionDetail | undefined
  onCancelBundleSubscription(): void
  scrollToCart?: () => void
  showUpgrade?: boolean
  showDowngrade?: boolean
  isSubscriptionInCart: boolean
}

const LevelAction: React.FC<LevelActionProps> = (props) => {
  const {
    onSetUnsubscribeConfirmationModal,
    onSetUnsubscribeAttentionConfirmationModal,
    onSetSubscriptionTypeModal,
    onResubscribeBundle,
    selectedSubscriptionDetail,
    onCancelBundleSubscription,
    scrollToCart,
    showUpgrade,
    showDowngrade,
    isSubscriptionInCart,
  } = props

  const { t } = useTranslation()

  if (!selectedSubscriptionDetail) return null

  const subscriptionDetailStatus = getSubscriptionDetailStatus(selectedSubscriptionDetail)

  const renderSubscribeButton = () => (
    <Button.Primary
      size='sm'
      disabled={isSubscriptionInCart}
      onClick={() => onSetSubscriptionTypeModal(true)}
    >
      {t('Market Data.Subscribe')}
    </Button.Primary>
  )

  const renderUnsubscribeButton = () => (
    <Button.Secondary
      size='sm'
      onClick={() => {
        if (isLastBusinessDay()) {
          onSetUnsubscribeAttentionConfirmationModal()
        } else {
          onSetUnsubscribeConfirmationModal()
        }
      }}
    >
      {t('Subscriptions.Unsubscribe')}
    </Button.Secondary>
  )

  const requestIsInProcess =
    subscriptionDetailStatus === SubscriptionRequestState.DeductingFunds ||
    subscriptionDetailStatus === SubscriptionRequestState.InProcess

  const requestIsPending = subscriptionDetailStatus === SubscriptionRequestState.Pending

  if (subscriptionDetailStatus === SubscriptionRequestState.Failed) {
    return renderSubscribeButton()
  }

  if (
    ((subscriptionDetailStatus !== 'subscribed' &&
      selectedSubscriptionDetail?.subscriptionRequest?.type.name ===
        SubscriptionRequestTypeEnum.AddNewSubscription) ||
      selectedSubscriptionDetail?.subscriptionRequest?.type.name ===
        SubscriptionRequestTypeEnum.UpdateExistingSubscription) &&
    !requestIsInProcess &&
    !requestIsPending
  ) {
    return (
      <div className='is-flex is-justify-content-center'>
        <Button.Secondary size='sm' onClick={() => onCancelBundleSubscription()}>
          {t('Cancel')}
        </Button.Secondary>
      </div>
    )
  }

  if (subscriptionDetailStatus === 'subscribed' && (showUpgrade || showDowngrade)) {
    return renderUnsubscribeButton()
  }

  if (requestIsInProcess) {
    return (
      <Badge type='yellow' contrast='high' size='sm'>
        {t('Subscriptions.Processing')}
      </Badge>
    )
  }

  if (subscriptionDetailStatus === SubscriptionRequestState.Pending) {
    if (isCancellationRequested(selectedSubscriptionDetail?.subscriptionRequest?.type.name)) {
      return (
        <Button.Secondary size='sm' onClick={onResubscribeBundle}>
          {t('Subscriptions.Resubscribe')}
        </Button.Secondary>
      )
    }

    return (
      <CancelOrProcessingButton
        onCancel={() => onCancelBundleSubscription()}
        scrollToCart={scrollToCart}
        subscriptionRequest={selectedSubscriptionDetail?.subscriptionRequest}
      />
    )
  }

  return renderSubscribeButton()
}

interface BundlePriceProps {
  showUpgrade?: boolean
  showDowngrade?: boolean
  bundle: SubscriptionBundleDto
  selectedSubscription: Subscription | undefined
  selectedSubscriptionDetail: SubscriptionDetail | undefined
  isExpanded: boolean
  handleOnSubscribeDetailChange: (subscription: Subscription, detail: SubscriptionDetail) => void
  setSelectedSubscriptionDetail: (detail: SubscriptionDetail) => void
  setPendingUnsubscriptionInfoModal: (isOpen: boolean) => void
}

const BundlePrice: React.FC<BundlePriceProps> = ({
  bundle,
  selectedSubscription,
  selectedSubscriptionDetail,
  isExpanded,
  handleOnSubscribeDetailChange,
  setSelectedSubscriptionDetail,
  setPendingUnsubscriptionInfoModal,
  showUpgrade,
  showDowngrade,
}) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()

  if (!selectedSubscription || !selectedSubscriptionDetail) {
    return null
  }

  if (selectedSubscription.details.length > 1 && isExpanded) {
    return (
      <BundleSubscriptionDetail
        showUpgrade={showUpgrade}
        showDowngrade={showDowngrade}
        selectedSubscription={selectedSubscription}
        handleOnSubscribeLevelChange={handleOnSubscribeDetailChange}
        setSelectedSubscriptionDetail={setSelectedSubscriptionDetail}
        selectedSubscriptionDetail={selectedSubscriptionDetail}
        setPendingUnsubscriptionInfoModal={setPendingUnsubscriptionInfoModal}
      />
    )
  }

  return (
    <>
      <Body2>{`${formatMoney(bundle.maxAmount)}/${t('Subscriptions.mth')}`} </Body2>
      <SubscriptionDetailMessage
        selectedSubscription={selectedSubscription}
        onSetPendingUnsubscriptionInfoModal={setPendingUnsubscriptionInfoModal}
      />
    </>
  )
}

interface SubscriptionBundleProps {
  bundle: SubscriptionBundleDto
  subscribedBundles: SubscriptionBundleDto[]
  scrollToCart?: () => void
  onUpdateSubscription(data: UpdateSubscriptionRequest, selectedItem?: SubscriptionDetail): void
  onAddCartItem(
    subscriptionDetailId: string,
    subscriptionRequestTypeId: SubscriptionRequestType
  ): void
  nonActiveSubscriptions: SubscriptionCartItem[]
}

interface TimeInForceContext {
  subscription: Subscription
  subscriptionDetail: SubscriptionDetail
}

export const SubscriptionBundle: React.FC<SubscriptionBundleProps> = ({
  bundle,
  subscribedBundles,
  scrollToCart,
  onUpdateSubscription,
  onAddCartItem,
  nonActiveSubscriptions,
}) => {
  const { scrollIntoView } = useScrollToElementIds()

  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | undefined>()
  const [selectedSubscriptionDetail, setSelectedSubscriptionDetail] = useState<SubscriptionDetail>()
  const [previousSelectedSubscriptionDetail, setPreviousSelectedSubscriptionDetail] =
    useState<SubscriptionDetail>()
  const [subscriptionTimeInForceType, setSubscriptionTimeInForceType] = useState<
    number | undefined
  >()
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    const [subscription] = bundle.subscriptions
    setSelectedSubscription(subscription)
    setSelectedSubscriptionDetail(getBadgeSubscriptionLevel(bundle))
  }, [bundle, subscribedBundles])

  const getSubscriptionRequestType = (subscription: Subscription): SubscriptionRequestType =>
    subscription?.activeSubscriptionRequest ||
    subscription?.subscriptionRequest?.state.name === SubscriptionRequestState.Pending
      ? SubscriptionRequestType.Update
      : SubscriptionRequestType.Add

  const canAddSubscription = (detail: SubscriptionDetail | undefined) =>
    !!detail &&
    !detail.activeSubscriptionRequest &&
    detail.subscriptionRequest?.state.name !== SubscriptionRequestState.Pending

  const handleSubscribe = (
    timeInForce: SubscriptionTimeInForce,
    subscription: Subscription,
    subscriptionDetail: SubscriptionDetail
  ) => {
    if (!canAddSubscription(subscriptionDetail)) return
    if (!isExpanded) return

    const subscriptionRequestTypeId = getSubscriptionRequestType(subscription)

    if (timeInForce === SubscriptionTimeInForce.BeginningOfNextMonth) {
      onUpdateSubscription({
        subscriptions: [
          {
            subscriptionDetailId: subscriptionDetail.id,
            subscriptionRequestTypeId,
          },
        ],
        subscriptionTimeInForceId: SubscriptionTimeInForce.BeginningOfNextMonth,
      })
    } else {
      onAddCartItem(subscriptionDetail.id, subscriptionRequestTypeId)
    }
  }

  const handleOnSelectSubscriptionTimeInForceType = (
    timeInForce: SubscriptionTimeInForce,
    ctx?: unknown
  ) => {
    const context = (ctx || {}) as TimeInForceContext
    const { subscription, subscriptionDetail } = context

    if (!subscription || !subscriptionDetail) return

    handleSubscribe(timeInForce, subscription, subscriptionDetail)
    setSubscriptionTimeInForceType(timeInForce)
  }

  const cancelTimeInForceSelection = () => {
    if (previousSelectedSubscriptionDetail) {
      setSelectedSubscriptionDetail(previousSelectedSubscriptionDetail)
    }
  }

  const timeInForceModal = useModal(SubscriptionTimeInForceModal)
  const detailModal = useModal(SubscriptionDetailModal)
  const pendingUnsubscriptionModal = useModal(PendingUnsubscriptionModal)
  const productServiceModal = useModal(ProductServiceModal)

  const handleOnSubscribeDetailChange = (
    subscription: Subscription,
    subscriptionDetail: SubscriptionDetail
  ) => {
    setPreviousSelectedSubscriptionDetail(selectedSubscriptionDetail)
    setSelectedSubscription(subscription)
    setSelectedSubscriptionDetail(subscriptionDetail)

    const downgrade = isLevelDowngrade(subscription, subscriptionDetail)

    timeInForceModal.open({
      subscriptionTimeInForceType,
      levelDowngrade: downgrade,
      context: { subscription, subscriptionDetail } as TimeInForceContext,
      onConfirm: handleOnSelectSubscriptionTimeInForceType,
      onCancel: cancelTimeInForceSelection,
    } as Partial<SubscriptionTimeInForceModalProps>)
  }

  const handleOnCancelBundleSubscription = () => {
    if (!selectedSubscriptionDetail) return
    onUpdateSubscription({
      subscriptions: [
        {
          subscriptionDetailId: selectedSubscriptionDetail.id,
          subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
        },
      ],
      overrideOpenPositionsForCancel: true,
      subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
    })
  }

  const handleOnClickUnsubscribe = () => {
    if (!selectedSubscriptionDetail) return

    onUpdateSubscription(
      {
        subscriptions: [
          {
            subscriptionDetailId: selectedSubscriptionDetail.id,
            subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
          },
        ],
        subscriptionTimeInForceId: SubscriptionTimeInForce.BeginningOfNextMonth,
      },
      selectedSubscriptionDetail
    )

    attentionModal.close()
    confirmationModal.close()
  }

  const setPendingUnsubscriptionInfoModal = (isOpen: boolean) => {
    if (isOpen && selectedSubscriptionDetail?.subscriptionRequest?.dueDate) {
      pendingUnsubscriptionModal.open({
        dueDate: selectedSubscriptionDetail.subscriptionRequest.dueDate,
      })
    }
  }

  const handleOnBundleSelected = (bundleToOpen: SubscriptionBundleDto) => {
    setIsExpanded(true)
    productServiceModal.open({ data: bundleToOpen })
  }

  const handleOnSetSelectedBundleToggle = (subscriptionId: string) => {
    if (!isExpanded) {
      scrollIntoView([`market-data-subscription-${subscriptionId}`])
    }
    setIsExpanded((prev) => !prev)
  }

  const handleOnResubscribeBundle = () => {
    if (!selectedSubscriptionDetail) return

    resubscribeModal.open({
      amount: selectedSubscriptionDetail.amount,
      onConfirm: () => {
        handleOnCancelBundleSubscription()
      },
    })
  }

  const isLevelSelectionDisabled = useMemo(
    () =>
      selectedSubscription &&
      (hasPendingUnsubscriptionRequest(selectedSubscription) ||
        hasActivatingSubscription(selectedSubscription) ||
        hasLevelInCart(selectedSubscription, nonActiveSubscriptions)),
    [selectedSubscription, nonActiveSubscriptions]
  )

  const allowUpgrade = useMemo(
    () =>
      selectedSubscription && !isLevelSelectionDisabled && allowSubUpgrade(selectedSubscription),
    [isLevelSelectionDisabled, selectedSubscription]
  )

  const allowDowngrade = useMemo(
    () =>
      selectedSubscription && !isLevelSelectionDisabled && allowSubDowngrade(selectedSubscription),
    [isLevelSelectionDisabled, selectedSubscription]
  )

  const pendingUpgrade = useMemo(
    () =>
      selectedSubscription && hasPendingUpgrade(selectedSubscription, selectedSubscriptionDetail),
    [selectedSubscription, selectedSubscriptionDetail]
  )

  const pendingDowngrade = useMemo(
    () =>
      selectedSubscription && hasPendingDowngrade(selectedSubscription, selectedSubscriptionDetail),
    [selectedSubscription, selectedSubscriptionDetail]
  )

  const showUpgrade = (allowUpgrade && !pendingUpgrade) || pendingDowngrade
  const showDowngrade = (allowDowngrade && !pendingDowngrade) || pendingUpgrade

  const isSubscriptionInCart = useMemo(
    () => hasLevelInCart(selectedSubscription, nonActiveSubscriptions),
    [selectedSubscription, nonActiveSubscriptions]
  )

  const id = bundle.id.toString()
  const productPackages = bundle.subscriptions.map((pkg) => ({ ...pkg, id: pkg.id.toString() }))

  const attentionModal = useModal(UnsubscribeAttentionModal, {
    onConfirm: handleOnClickUnsubscribe,
  })
  const confirmationModal = useModal(UnsubscribeConfirmationModal, {
    onConfirm: handleOnClickUnsubscribe,
  })
  const cancelPendingModal = useModal(ConfirmCancelSubscriptionModal, {
    onConfirm: handleOnCancelBundleSubscription,
  })
  const resubscribeModal = useModal(ResubscribeModal)

  const cancelPendingModalOpen = () => {
    cancelPendingModal.open()
  }

  return (
    <ExpandableRow
      id={`market-data-subscription-${bundle.id}`}
      row={{
        ...bundle,
        id,
        subRows: productPackages,
      }}
      isExpanded={isExpanded}
      hasTruncate={false}
      onToggle={() => handleOnSetSelectedBundleToggle(id)}
      renderParent={(p) => [
        {
          value: (
            <Box flex direction='column' gap='x4'>
              <Body2 medium>
                <Link
                  to='#'
                  onClick={(event) => {
                    event.preventDefault()
                    handleOnBundleSelected(bundle)
                  }}
                >
                  {p?.name}
                </Link>{' '}
                🔥
              </Body2>
              {p?.subName && (
                <Body2 wrap='wrap' color='text-med-em'>
                  {p.subName}
                </Body2>
              )}
            </Box>
          ),
          align: 'left',
        },
        {
          value: !isExpanded ? (
            <Body2>
              <PriceRange minAmount={p?.minAmount} maxAmount={p?.maxAmount} />
            </Body2>
          ) : undefined,
          align: 'left',
        },
        {
          value: (
            <BundleStatus
              bundle={bundle}
              subscribedBundles={subscribedBundles}
              onSetIsExpanded={setIsExpanded}
              isExpanded={isExpanded}
            />
          ),
          align: 'center',
        },
      ]}
      renderSubRow={(subscription: Subscription) => [
        {
          value: (
            <Box flex direction='column' gap='x4'>
              <Box flex direction='row'>
                {isProductStatus(subscription) && (
                  <Box mr='x8'>
                    <ProductStatusIcon subscription={subscription} />
                  </Box>
                )}
                <Box>
                  <Body2 medium>{bundle.name}</Body2>
                </Box>
              </Box>
              <Box>
                <Body2 className={styles.dataDescription}>
                  {selectedSubscriptionDetail?.description}
                </Body2>
              </Box>
            </Box>
          ),
          align: 'left',
        },
        {
          value: (
            <Box flex direction='row' gap='x4' align='center'>
              <BundlePrice
                bundle={bundle}
                handleOnSubscribeDetailChange={handleOnSubscribeDetailChange}
                isExpanded={isExpanded}
                selectedSubscription={selectedSubscription}
                setPendingUnsubscriptionInfoModal={setPendingUnsubscriptionInfoModal}
                selectedSubscriptionDetail={selectedSubscriptionDetail}
                setSelectedSubscriptionDetail={setSelectedSubscriptionDetail}
                showUpgrade={showUpgrade}
                showDowngrade={showDowngrade}
              />
            </Box>
          ),
          align: 'left',
        },
        {
          value: (
            <Box flex direction='row' gap='x4'>
              <LevelAction
                isSubscriptionInCart={isSubscriptionInCart}
                onResubscribeBundle={handleOnResubscribeBundle}
                onSetUnsubscribeAttentionConfirmationModal={attentionModal.open}
                onSetUnsubscribeConfirmationModal={confirmationModal.open}
                onSetSubscriptionTypeModal={(state) => {
                  if (state && subscription.details.length > 1) {
                    return detailModal.open({
                      subscription,
                      selectedSubscriptionDetail: selectedSubscription?.details.find(
                        (detail) => !!detail.activeViaBundleSubscriptionRequest
                      ),
                      isSelectEnabled: true,
                      onConfirm: (subscriptionDetail) => {
                        if (!subscriptionDetail) return

                        setPreviousSelectedSubscriptionDetail(selectedSubscriptionDetail)
                        setSelectedSubscription(subscription)
                        setSelectedSubscriptionDetail(subscriptionDetail)

                        const downgrade = isLevelDowngrade(subscription, subscriptionDetail)

                        timeInForceModal.open({
                          subscriptionTimeInForceType,
                          levelDowngrade: downgrade,
                          context: {
                            subscription,
                            subscriptionDetail,
                          } as TimeInForceContext,
                          onConfirm: handleOnSelectSubscriptionTimeInForceType,
                          onCancel: cancelTimeInForceSelection,
                        } as Partial<SubscriptionTimeInForceModalProps>)
                      },
                    })
                  }

                  if (state) {
                    const detail =
                      selectedSubscriptionDetail ||
                      getSubscriptionDefaultLevel(subscription) ||
                      subscription.details[0]

                    setPreviousSelectedSubscriptionDetail(selectedSubscriptionDetail)
                    setSelectedSubscription(subscription)
                    setSelectedSubscriptionDetail(detail)

                    const downgrade = isLevelDowngrade(subscription, detail)

                    timeInForceModal.open({
                      subscriptionTimeInForceType,
                      levelDowngrade: downgrade,
                      context: { subscription, subscriptionDetail: detail } as TimeInForceContext,
                      onConfirm: handleOnSelectSubscriptionTimeInForceType,
                      onCancel: cancelTimeInForceSelection,
                    } as Partial<SubscriptionTimeInForceModalProps>)
                  }
                }}
                selectedSubscriptionDetail={selectedSubscriptionDetail}
                onCancelBundleSubscription={cancelPendingModalOpen}
                scrollToCart={scrollToCart}
                showUpgrade={showUpgrade}
                showDowngrade={showDowngrade}
              />
            </Box>
          ),
          align: 'center',
        },
      ]}
    />
  )
}
